import {SIZE_MULTIPLIER} from "./utils";

export const trackingLinkValidationRules = {
    pattern: {
        value: /^.{0,255}$/,
        message: 'Invalid Tracking Link. Max 255 characters expected',
    },
};

export const consignmentNumberValidationRules = {
    pattern: {
        value: /^.{0,75}$/,
        message: "Invalid Consignment Number. Max 75 characters expected"
    }
};

// export const phoneNumberValidationRules = {
//     pattern: {
//         value: /^$|^\+\d{2}(\s)?\d{3}(\s)?\d{3}(\s)?\d{3}$/,
//         message: "Invalid Phone. +xx xxx xxx xxx format expected"
//     }
// };

export const phoneNumberValidationRules = {
  pattern: {
    value: new RegExp(
      [
        '^$|', // allow empty
        '^\\+?(', // optional plus, then one of:
          // --- Australian numbers ---
          '(61\\s?(?:[0-9]{1,4}\\s?)?[0-9]{3}\\s?[0-9]{3,4}\\s?[0-9]{0,4})',
          '|', // or
          // --- US numbers ---
          '(1[-\\s]?(?:\\(\\d{3}\\)|\\d{3})[-\\s]?\\d{3}[-\\s]?\\d{4})',
        ')$',
      ].join('')
    ),
    message:
      "Invalid phone number. Use formats like +61 123 456 789 or +1 (555) 123-4567.",
  },
};

export const mobileNumberValidationRules = {
    required: 'Mobile Number is required',
    pattern: {
        value: new RegExp(
          [
            '^\\+?(', // optional plus, then one of:
              // --- Australian numbers ---
              '(61\\s?(?:[0-9]{1,4}\\s?)?[0-9]{3}\\s?[0-9]{3,4}\\s?[0-9]{0,4})',
              '|', // or
              // --- US numbers ---
              '(1[-\\s]?(?:\\(\\d{3}\\)|\\d{3})[-\\s]?\\d{3}[-\\s]?\\d{4})',
            ')$',
          ].join('')
        ),
        message: "Invalid mobile number. Use formats like +61 123 456 789 or +1 (555) 123-4567.",
    },
};

/**
 * The number on its own, with the country code chosen from a dropdown beside it rather than typed.
 *
 * phoneNumberValidationRules above has to recognise the country code inside the string, which is why it
 * only ever admitted +61 and +1 — every other country was a validation error however correctly it was
 * written. Once the code comes from a fixed list it cannot be wrong, so all that is left to check is the
 * national part: digits, optionally grouped, and a length E.164 permits (15 digits total, minus a code of
 * up to 3). The lookahead counts digits while allowing spaces, dashes and brackets between them.
 *
 * A leading 0 is rejected on purpose. It is the domestic trunk prefix, dropped in every international
 * format, so "+61 0400 111 222" is not a number anyone can dial — and a legacy record holding a local
 * "0400 111 222" is exactly the case that reaches this field with the 0 still attached. Refusing it with
 * the correction in the message is more use than accepting a number that will not connect.
 */
export const nationalPhoneNumberValidationRules = {
    pattern: {
        value: /^$|^(?![\s\-()]*0)(?=(?:[\s\-()]*\d){5,14}[\s\-()]*$)[\d\s\-()]+$/,
        message: "Invalid number. Enter 5 to 14 digits after the country code, with no leading 0 — e.g. 400 111 222.",
    },
};

export const twoDecimalPrecisionNumberValidationRules = {
    valueAsNumber: true,
    pattern: {
        value: /^$|^\d{1,8}(\.\d{1,2})?$/,
        message:
            "Must be a number with up to 8 digits before the decimal and up to 2 digits after (e.g. 12345678.90).",
    },
    validate: (val) =>
        !val ||
        Number.isNaN(val) ||
        // otherwise enforce your range:
        (val >= 0 && val <= 99999999.99) ||
        "Value must be between 0 and 99,999,999.99",
};

/**
 * Generate validation rules for integer or fixed-decimal floats
 * @param {number} decimals - Number of decimals allowed (0 = integer only, 1 = one decimal, etc.)
 */
export const numberValidationRules = (decimals = 0) => {
    // Regex: up to 8 digits before decimal, and exactly `decimals` digits after decimal if > 0
    const regex =
        decimals === 0
            ? /^$|^\d{1,8}$/ // integer only
            : new RegExp(`^$|^\\d{1,8}(\\.\\d{1,${decimals}})?$`);

    const maxValue = decimals === 0 ? 99999999 : 99999999 + (1 - Math.pow(10, -decimals));

    return {
        valueAsNumber: true,
        pattern: {
            value: regex,
            message:
                decimals === 0
                    ? "Must be an integer with up to 8 digits (e.g. 12345678), greater than 0."
                    : `Must be a number with up to 8 digits before the decimal and up to ${decimals} digits after (e.g. 12345678.${"9".repeat(decimals)}), greater than 0..`,
        },
        validate: (val) =>
            !val ||
            Number.isNaN(val) ||
            (val >= 0 && val <= maxValue) ||
            `Value must be between 0 and ${maxValue.toLocaleString()}`,
    };
};

export const sizeUnitValidationRules = {
    pattern: {
        value: new RegExp(`^$|^(${Object.keys(SIZE_MULTIPLIER).join("|")})$`),
        message: `Must be empty or one of: ${Object.keys(SIZE_MULTIPLIER).join(", ")}`,
    },
};

export const emailValidationRules = {
    pattern: {
        value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/,
        message: 'Invalid email format. It should be in the format example@domain.com',
    },
};

export const descriptionMessageValidationRules = (maxCharactersNo) => {
    return {
        required: 'Additional Instructions are required',
        validate: (val) => !!val?.trim() || 'Additional Instructions are required',
        maxLength: { value: maxCharactersNo, message: `Invalid message or description. Max ${maxCharactersNo} characters expected` }
    };
};

export const firstNameValidationRules = {
    pattern: {
        value: /^.{0,100}$/,
        message: 'Invalid First Name. Max 100 characters expected',
    },
};

export const lastNameValidationRules = {
    pattern: {
        value: /^.{0,100}$/,
        message: 'Invalid Last Name. Max 100 characters expected',
    },
};

export const contactNameValidationRules = {
    pattern: {
        value: /^.{0,255}$/,
        message: 'Invalid Contact Name. Max 255 characters expected',
    },
};

export const companyNameValidationRules = {
    pattern: {
        value: /^.{0,255}$/,
        message: 'Invalid Company Name. Max 255 characters expected',
    },
};
export const addressValidationRules = {
    pattern: {
        value: /^.{0,500}$/,
        message: 'Invalid Address. Max 500 characters expected',
    },
};

export const postalCodeValidationRules = {
    pattern: {
        value: /^.{1,15}$/,
        message: 'Invalid Postal Code. Max 15 characters expected',
    },
};

export const cityValidationRules = {
    pattern: {
        value: /^.{0,255}$/,
        message: 'Invalid City. Max 255 characters expected',
    },
};

export const stateValidationRules = {
    pattern: {
        value: /^.{1,255}$/,
        message: 'Invalid State. Max 255 characters expected',
    },
};

export const countryValidationRules = {
    pattern: {
        value: /^.{1,255}$/,
        message: 'Invalid Country. Max 255 characters expected',
    },
};

export const ingestionSiteValidationRules = {
    required: "Please select ingestion site"
};

export const urgencyLevelValidationRules = {
    pattern: {
        value: /^(low|medium|high|severe)$/,
        message: 'Urgency must be Low, Medium, High, or Severe',
    }
};

// Used when billing === 'other' — the description is required.
// Pass `getBillingDescriptionValidationRules(watch)` to register().
export const getBillingDescriptionValidationRules = (watch) => ({
    validate: (val) =>
        watch('billing') !== 'other' ||
        !!val?.trim() ||
        'Description is required when billing is set to Other',
});

export const tapeTypeValidationRules = {
    required: 'Media Type is required',
};

export const tapeNumberValidationRules = {
    required: 'Number is required',
    min: {value: 1, message: 'Must be at least 1'},
};

export const tapeFormatValidationRules = {
    required: 'Backup Format is required',
};

export const tapeNumberOfBoxesValidationRules = {
    required: 'Number of Boxes is required',
    min: {value: 1, message: 'Must be at least 1'},
    max: {value: 30, message: 'Maximum 30 boxes allowed'},
    pattern: {
        value: /^\d+$/,
        message: 'Must be a positive integer',
    },
};

export const quoteReferenceIdValidationRules = {
    maxLength: {value: 50, message: 'Quote Reference ID must be 50 characters or fewer'},
};

export const projectScopeValidationRules = {
};

export const projectDeliverablesValidationRules = {
};

export const storageDurationValidationRules = {
    pattern: {
        value: /^\d+$/,
        message: 'Storage duration must be a positive integer',
    },
};

// Required when shippingMode === 'tapeark'
export const getShippingPartnerValidationRules = (watch) => ({
    validate: (val) =>
        watch('shippingMode') !== 'tapeark' ||
        !!val?.trim() ||
        'Shipping Partner is required',
});

// Optional — format validated only when a value is provided
export const getReturnAddressPhoneValidationRules = () => ({
    ...phoneNumberValidationRules,
});

export const getReturnAddressMobileValidationRules = (watch) => {
    return {
        ...phoneNumberValidationRules,
        validate: (val) =>
            watch('ingestion.returnTo') !== false ||
            !!val?.trim() ||
            'Mobile number is required for return address',
    };
};
